import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import LiveCourseMainCard from '../LiveCourse/LiveCourseMainCard';
import Loader from '../Loader/Loader';
import type { Course, LiveCourse } from '@/types/course';
import { useSelectedCategories } from '@/redux/selectors/categorySelector';
import { getCourses } from '@/services/strapi/strapiCategoryService';
import ArrowRightIcon from '../Icons/Action/ArrowRightIcon';
import BookingModal from '../Modal/BookingModal';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

export const mapCourseToLiveCourse = (course: Course): LiveCourse => {
  return {
    imageSrc: course.attributes.cover?.data?.attributes.url || '',
    title: course.attributes.title,
    date: course.attributes.date,
    description: course.attributes.description,
    expertName: course.attributes.expert?.data?.attributes?.name || '',
    expertImage: course.attributes.expert?.data?.attributes?.picture?.data?.attributes?.url || '',
    time_range: course.attributes.time_range,
    expertExternalId: course.attributes.expert?.data?.attributes?.external_id || '',
    id: course.id,
    languages: course.attributes.languages?.data
      ?.map((language) => language.attributes.name)
      .join(', '),
  };
};

const NextArrow = ({ onClick, isVisible }: { onClick?: () => void; isVisible: boolean }) => (
  <div
    className={`absolute right-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    onClick={onClick}
  >
    <ArrowRightIcon width={20} height={20} />
  </div>
);

const PrevArrow = ({ onClick, currentSlide }: { onClick?: () => void; currentSlide: number }) =>
  currentSlide > 0 ? (
    <div
      className="absolute left-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center"
      onClick={onClick}
    >
      <ArrowRightIcon width={20} height={20} className="transform rotate-180" />
    </div>
  ) : null;

const LiveCourseSlider: React.FC = () => {
  const [liveCourses, setLiveCourses] = useState<LiveCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const selectedCategories = useSelectedCategories();
  const [selectedCourse, setSelectedCourse] = useState<LiveCourse | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const locale = useSelectedLanguage();

  const openModal = (course: LiveCourse, isSuccess?: boolean) => {
    setSelectedCourse(course);
    isSuccess && setIsSuccess(isSuccess);
  };

  const closeModal = () => {
    setSelectedCourse(null);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const categorySlugs = selectedCategories.filter((category) => category !== 'all');
        const response = await getCourses(10, ['date:asc'], 0, categorySlugs, locale);

        const mappedCourses = response.data.map(mapCourseToLiveCourse);
        setLiveCourses(mappedCourses);
      } catch (error) {
        console.error('Error fetching live courses:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    void fetchCourses();
  }, [selectedCategories]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = liveCourses.length;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1.06,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <NextArrow isVisible={currentSlide < slideCount - 1} />,
    prevArrow: <PrevArrow currentSlide={currentSlide} />,
    beforeChange: (current: number, next: number) => setCurrentSlide(Math.round(next)),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1.06,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.06,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) return <Loader position={'absolute'} />;
  if (error) return <div>Error loading live courses</div>;
  if (liveCourses.length === 0) return null;
  return (
    <div className="relative p-5 md:p-12 pr-0 mb-3 w-full">
      <div className="relative">
        <h2 className="text-4xl">Live Courses</h2>
      </div>
      <Slider {...settings} className="mt-4">
        {liveCourses.map((course, index) => (
          <div key={index}>
            <LiveCourseMainCard course={course} onOpenModal={() => openModal(course)} />
          </div>
        ))}
      </Slider>
      {selectedCourse && (
        <BookingModal
          isOpen={!!selectedCourse}
          onClose={closeModal}
          course={selectedCourse}
          initialBookingSuccess={isSuccess}
        />
      )}
    </div>
  );
};

export default LiveCourseSlider;
